/* eslint-disable react-hooks/exhaustive-deps */
import { FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import styled from "styled-components";
import * as Sentry from "@sentry/react";
import { mutate } from "swr";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
  Switch,
} from "@material-ui/core";
import pick from "utils/pick";
import ButtonWithLoading from "components/ButtonWithLoading";
import apiRequest, { getErrorCode } from "utils/apiRequestWithErrorCode";
import { isBlank } from "utils/string";
import SelectAliases from "./SelectAliases";
import ServerSelector from "./ServerSelector";

import { Domain, UpdateDomainBody } from "@dashboard-v3/api";
import useOrganization from "utils/useOrganization";

export type Params = {
  domain: Domain;
  isOpen: boolean;
  onClose: () => void;
};

type Errors = Partial<{ [key in "aliases" | "mxLookup" | "server"]: string }>;

export default function DomainConfigForm(props: Params) {
  const { domain: initValue, isOpen, onClose } = props;
  const { t } = useTranslation("orgAndDomains");
  const { organization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const [saving, setSaving] = useState(false);
  const [changes, setChanges] = useState<UpdateDomainBody>(
    pickEditableFields(initValue)
  );
  const [errors, setErrors] = useState<Errors>({});

  const domainName = initValue.domain;

  const handleChange = event => {
    const { name, value, checked } = event.target;

    setErrors(prev => {
      delete prev[name];
      return prev;
    });

    setChanges(current => {
      return { ...current, [name]: isCheckbox(name) ? checked : value };
    });
  };

  async function validateFieldsOnSubmit() {
    if (isBlank(changes.server)) {
      setErrors(prev => ({
        ...prev,
        server: t("error.domainServer.required"),
      }));

      return false;
    }

    try {
      const isValidHostOrIp = await apiRequest<boolean>(
        "GET",
        `/domains/validate-server/${encodeURIComponent(changes.server)}`
      );

      if (!isValidHostOrIp) {
        setErrors(prev => ({
          ...prev,
          server: t("error.domainServer.invalid"),
        }));

        return false;
      }
    } catch (error) {
      enqueueSnackbar(t("error.serverValidation"), { variant: "error" });
      Sentry.captureException(error, {
        extra: {
          organizationId: organization.id,
          server: changes.server,
        },
      });

      return false;
    }

    return true;
  }

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    setSaving(true);

    const isValid = await validateFieldsOnSubmit();

    if (!isValid) {
      return setSaving(false);
    }

    try {
      await apiRequest<Domain>("PUT", `/domains/${domainName}`, changes);

      mutate("/domains");
      mutate("/manual-domains");
      onClose();
    } catch (error) {
      console.error(error);
      Sentry.captureException(error, { extra: { domainName, changes } });
      const code = getErrorCode<"INVALID_ALIASES">(error);

      const errorMessage = t([
        `error.editDomain.${code}`,
        `error.editDomain.SYSTEM_FAILURE`,
      ]);

      enqueueSnackbar(errorMessage, {
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  }

  function onServerChange(option: string) {
    setErrors(prev => {
      delete prev.server;
      return prev;
    });

    setChanges(prev => ({ ...prev, server: option || "" }));
  }

  return (
    <Dialog open={isOpen} onClick={e => e.stopPropagation()} onClose={onClose}>
      <DialogTitle>
        {t("list.editSomeDomain", { value: domainName })}
      </DialogTitle>
      <form
        data-testid="editDialogForm"
        style={{ minWidth: "450px" }}
        onSubmit={handleSubmit}
      >
        <DialogContent>
          <ServerSelector
            domainName={domainName}
            value={changes.server}
            onChange={onServerChange}
            hasError={Boolean(errors?.server)}
            errorMsg={errors?.server}
          />
          <StyledTextField
            type="text"
            name="relayServer"
            label={t("list.relayServer")}
            variant="outlined"
            fullWidth
            value={changes.relayServer}
            onChange={handleChange}
            inputProps={{
              "data-testid": "relayServerInput",
            }}
          />
          <SelectAliases
            value={changes.aliases}
            onChange={values => {
              setChanges(changes => ({ ...changes, aliases: values }));
            }}
          />
          <FormControlLabel
            label={t("list.rcptLookup")}
            control={
              <Switch
                checked={changes.rcptLookup}
                onChange={handleChange}
                name="rcptLookup"
                color="primary"
              />
            }
          />
          <FormControlLabel
            label={t("list.mxLookup")}
            control={
              <Switch
                checked={changes.mxLookup}
                onChange={handleChange}
                name="mxLookup"
                color="primary"
              />
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={onClose}
            color="primary"
            data-testid="closeEditDialog"
          >
            {t("common:cancel")}
          </Button>
          <ButtonWithLoading
            color="primary"
            disableElevation
            type="submit"
            variant="contained"
            data-testid="saveEditBtn"
            loading={saving}
            disabled={Boolean(Object.keys(errors).length)}
          >
            {t("common:save")}
          </ButtonWithLoading>
        </DialogActions>
      </form>
    </Dialog>
  );
}

function pickEditableFields(domain: Domain): UpdateDomainBody {
  return pick(domain, [
    "aliases",
    "mxLookup",
    "rcptLookup",
    "relayServer",
    "server",
  ]);
}

const StyledTextField = styled(TextField)`
  margin-bottom: 16px;
`;

const isCheckbox = (fieldName: string) =>
  fieldName === "rcptLookup" || fieldName === "mxLookup";
